<template>
	<div class="footer-pusher" :class="route?.name === 'home' ? 'root-home-page' : null">
		<template v-if="hasBrowserSupport()">
			<global-svg-symbols />
			<section class="banner-app-moved" aria-label="MTG Deck Builder Migration Information">
				<div class="wrap">
					<h2>
						🚛 Moved to
						<a href="https://mtgcarpenter.com" target="_blank" class="no-line-break"
							>MTGCarpenter.com<svg aria-hidden="true">
								<use href="#open-in-new-icon" />
							</svg>
						</a>
					</h2>
					<div>
						<p>
							MTG Deck Builder is dead. Long live
							<strong>MTG Carpenter</strong>!
						</p>
						<p>It’s the same app, but with a new name and web address, and will be actively maintained going forward.</p>
						<p>
							Moving your decks from here is easy:
							<router-link to="/archive-decks"><strong>Save them into a deck archive file</strong></router-link
							>, then
							<a href="https://mtgcarpenter.com/create-deck" target="_blank">
								replicate them on
								<span class="no-line-break"
									>MTG Carpenter<svg aria-hidden="true"><use href="#open-in-new-icon" /></svg>&#8205;</span
								></a
							>. It’ll only take 10 seconds to move all your decks at once!
						</p>
					</div>
				</div>
			</section>
			<app-header />
			<main>
				<router-view />
			</main>
			<app-footer />
			<scroll-to-top />
		</template>
		<browser-unsupported v-else />
	</div>
</template>

<script setup>
import { onMounted, watch } from 'vue'
import { useHead } from '@unhead/vue'
import { useRoute } from 'vue-router'
import router from '@/js/router.js'
import { useStore } from 'vuex'
import { event as gtagEvent } from 'vue-gtag'
import GlobalSvgSymbols from '@/components/GlobalSvgSymbols.vue'
import AppHeader from '@/components/AppHeader.vue'
import AppFooter from '@/components/AppFooter.vue'
import ScrollToTop from '@/components/ScrollToTop.vue'
import BrowserUnsupported from '@/components/BrowserUnsupported.vue'
import { useServiceWorkerUpdates } from '@/composables/useServiceWorkerUpdates'

const route = useRoute()
const store = useStore()

let pageIsReloading = false

useHead({
	title: 'MTG Deck Builder by Iristorm Design',
})

useHead({
	meta: [
		{
			name: 'robots',
			content: 'noindex, follow',
		},
	],
})

function hasBrowserSupport() {
	const techTest = [
		'HTMLDialogElement' in window,
		(() => {
			try {
				const storageKey = 'localStorage_test'

				window.localStorage.setItem(storageKey, '1')
				window.localStorage.removeItem(storageKey)

				return true
			} catch {
				return false
			}
		})(),
	]

	return techTest.every((test) => test)
}

function autoReloadOnUpdate() {
	if (store.state.waitingToApplyUpdate && !pageIsReloading) {
		store.commit('updateJustInstalled', true)

		pageIsReloading = true

		window.location.reload()
	}
}

function onVisibilityChange() {
	if (document.visibilityState === 'visible') {
		autoReloadOnUpdate()
	}
}

useServiceWorkerUpdates()
autoReloadOnUpdate()

onMounted(() => {
	document.addEventListener('visibilitychange', onVisibilityChange)
})

router.isReady().then(() => {
	if (store.state.updateJustInstalled) {
		gtagEvent('app_update_activated', {})
		store.commit('updateJustInstalled', false)
	}
})

watch(() => route.fullPath, autoReloadOnUpdate)
</script>

<style lang="scss">
@use '@/sass/index.scss';
</style>
